import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { Mail } from "lucide-react";
import { useAuthStore } from "../hooks";
import { PATHS } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthStandaloneShell } from "../components";

const ForgotPasswordPage = observer(() => {
  const navigate = useNavigate();
  const { t } = useTran();
  const inputsRef = useRef([]);
  const {
    forgotStep,
    forgotEmail,
    forgotOtp,
    loading,
    forgotReset,
    setForgotEmail,
    setForgotOtpAt,
    requestPasswordReset,
    verifyOtp,
  } = useAuthStore();

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    await requestPasswordReset();
  };

  const handleOtpChange = (index, value) => {
    setForgotOtpAt(index, value);
    const nextRef = inputsRef.current[index + 1];
    if (nextRef && value) nextRef.focus();
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !forgotOtp[index] && index > 0) {
      const prevRef = inputsRef.current[index - 1];
      if (prevRef) prevRef.focus();
    }
  };

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    const success = await verifyOtp();
    if (success) {
      navigate(PATHS.RESET_PASSWORD);
    }
  };

  return (
    <AuthStandaloneShell>
      {forgotStep === "email" ? (
        <form
          onSubmit={handleEmailSubmit}
          className="flex flex-col items-center"
        >
          <h1 className="mb-2 text-center text-2xl font-semibold text-primary sm:text-3xl">
            {t("Forgot Password")}
          </h1>
          <p className="mb-6 text-center text-sm text-muted-foreground">
            {t("Enter your registered email address to receive a reset code.")}
          </p>

          <div className="relative mb-4 w-full">
            <Input
              type="email"
              value={forgotEmail}
              onChange={(event) => setForgotEmail(event.target.value)}
              placeholder={t("Email")}
              required
              className="pl-10"
            />
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <Mail className="h-5 w-5" />
            </span>
          </div>

          {forgotReset?.serverError && (
            <p className="mb-4 text-center text-sm text-red-600">
              {forgotReset.serverError}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading || !forgotEmail}
            className="w-full rounded-full bg-primary hover:bg-primary/90"
          >
            {loading ? t("Sending...") : t("Send Reset Code")}
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(PATHS.LOGIN)}
            className="mt-4 w-full"
          >
            {t("Back to Login")}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleOtpSubmit} className="flex flex-col items-center">
          <h1 className="mb-2 text-center text-2xl font-semibold text-primary sm:text-3xl">
            {t("Verify Code")}
          </h1>
          <p className="mb-6 text-center text-sm text-muted-foreground">
            {t("Enter the 6-digit code sent to your email.")}
          </p>

          <div className="mb-4 grid w-full grid-cols-6 gap-2 sm:gap-3">
            {forgotOtp.map((digit, index) => (
              <Input
                key={index}
                ref={(element) => (inputsRef.current[index] = element)}
                inputMode="numeric"
                autoComplete={index === 0 ? "one-time-code" : undefined}
                maxLength={1}
                value={digit}
                onChange={(event) => handleOtpChange(index, event.target.value)}
                onKeyDown={(event) => handleOtpKeyDown(index, event)}
                className="h-12 w-full text-center text-lg"
              />
            ))}
          </div>

          {forgotReset?.serverError && (
            <p className="mb-4 text-center text-sm text-red-600">
              {forgotReset.serverError}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading || forgotOtp.some((digit) => !digit)}
            className="w-full rounded-full bg-primary hover:bg-primary/90"
          >
            {loading ? t("Verifying...") : t("Verify Code")}
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(PATHS.LOGIN)}
            className="mt-4 w-full"
          >
            {t("Back to Login")}
          </Button>
        </form>
      )}
    </AuthStandaloneShell>
  );
});

export default ForgotPasswordPage;
